import React, { useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatures } from '../contexts/FeaturesContext';
import { applicantBaseForRole, shouldGateApplicant } from '../utils/applicantGating';
import api from '../services/api';
import notificationService from '../services/notificationService';
import verifierAgreementService from '../services/verifierAgreementService';
import adminApplicationsService from '../services/adminApplicationsService';
import OfflineStatusBar from './common/OfflineStatusBar';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  Collapse,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  Badge
} from '@mui/material';
import {
  Dashboard,
  Build,
  People,
  AccountBalance,
  Assignment,
  AttachMoney,
  Assessment,
  AdminPanelSettings,
  TrendingUp,
  Settings,
  Security,
  Analytics,
  Menu as MenuIcon,
  Person,
  ExitToApp,
  Notifications,
  Message,
  Search,
  SupervisorAccount,
  Description,
  CompareArrows,
  Store,
  RequestQuote,
  PhotoLibrary,
  Folder,
  VerifiedUser,
  ToggleOn,
  WhatsApp,
  SmartToy,
  Inventory,
  Inventory2,
  HomeWork,
  Payment,
  Gavel,
  ShoppingCart,
  Star,
  Storefront,
  ReceiptLong,
  Engineering,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import EnhancedNotificationCenter from './Notifications/EnhancedNotificationCenter';
import { brand, status, text, bg, border } from '../theme/colors';

import PublicHeader from './PublicHeader';
const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Which nav groups the user has explicitly toggled. Unlisted groups fall
  // back to "open when one of my children is the current page".
  // Which nav group is expanded. `undefined` means "not touched yet — derive
  // from the current route"; a string is that group; null is all closed.
  // Single-valued so the groups behave as an accordion.
  const [openGroup, setOpenGroup] = useState(undefined);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bidReviewCount, setBidReviewCount] = useState(0);
  // Pending-application counts for the admin sidebar badges.
  const [pendingApplications, setPendingApplications] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [agreementApplicable, setAgreementApplicable] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  // The admin surface sits on a near-white ground so its shadowed panels and
  // their grey headers both read clearly; bg.page was too close to bg.subtle.
  const isAdminSurface = location.pathname.startsWith('/admin/home');
  const navigate = useNavigate();
  const { user, isAuthenticated, sessionValidated, logout } = useAuth();
  const features = useFeatures();
  const isPublicShortlink = location.pathname.startsWith('/p/contractor/') || location.pathname.startsWith('/p/verifier/');

  // Header identity: the auth user keeps profile fields nested under `user.profile`
  // (camelCase), so the old `user?.first_name` always fell back to "User". Show the
  // real name as the primary line and the company (when present) on the second line
  // in place of the role.
  const _p = user?.profile || {};
  const _fullName = [
    _p.firstName || user?.first_name || user?.firstName,
    _p.lastName || user?.last_name || user?.lastName,
  ].filter(Boolean).join(' ').trim();
  const _company = _p.companyName || user?.company_name || user?.companyName || '';
  const _roleLabel = user?.role === 'client' ? 'Project Owner' : (user?.role?.replace('_', ' ') || 'User');
  const headerName = _fullName || _company || user?.name || (user?.email ? user.email.split('@')[0] : '') || 'User';
  // Second line: prefer the company; otherwise fall back to the role so it's never blank.
  const headerSubtitle = (_company && _company !== headerName) ? _company : _roleLabel;

  // Fetch all badge counts and keep them fresh via socket + 60s fallback poll.
  // Keyed on user id/role (NOT the whole user object) so a session refresh that
  // replaces the user reference doesn't tear down and re-fire this effect —
  // which was causing a storm of unread-count requests.
  React.useEffect(() => {
    if (!isAuthenticated || !user) return undefined;
    const role = user.role;

    const fetchUnreadMessages = async () => {
      try {
        const response = await api.get('/conversations/unread-count');
        if (response.data.success) setUnreadMessages(response.data.data?.unread_conversations || 0);
      } catch { /* silent */ }
    };

    const fetchApplicationCounts = async () => {
      try {
        const res = await adminApplicationsService.counts();
        setPendingApplications(res?.data?.pending || null);
      } catch { /* silent — badges just don't render */ }
    };

    const refreshAll = () => {
      fetchUnreadCount();
      fetchUnreadMessages();
      if (role === 'client') fetchBidReviewCount();
      if (role === 'admin') fetchApplicationCounts();
    };

    // Initial fetches
    refreshAll();
    if (['verifier', 'professional_verifier'].includes(role)) {
      verifierAgreementService.getAgreement()
        .then((data) => setAgreementApplicable(data.applicable === true))
        .catch(() => { /* fail-open: don't show tab on error */ });
    }

    // 60-second fallback poll (socket handles immediate updates)
    const interval = setInterval(refreshAll, 60000);

    // Socket subscription: coalesce bursts of notification events into a single
    // debounced refresh so a flood of events can't storm the unread-count APIs.
    let debounceTimer = null;
    const unsubNotification = notificationService.on('notification', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(refreshAll, 2000);
    });

    // Pages that consume unread items (opening a Messages thread, marking a
    // notification read) fire this so badges drop immediately instead of
    // waiting for the 60s poll.
    const onLocalRefresh = () => refreshAll();
    window.addEventListener('eqaya:unread-refresh', onLocalRefresh);

    return () => {
      clearInterval(interval);
      clearTimeout(debounceTimer);
      unsubNotification();
      window.removeEventListener('eqaya:unread-refresh', onLocalRefresh);
    };
  }, [isAuthenticated, user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBidReviewCount = async () => {
    try {
      const [bidsResponse, verifierProposalsResponse] = await Promise.all([
        api.get('/bids/client/me'),
        api.get('/client/verifier-proposals', { params: { clientId: user.id } })
      ]);

      const contractorBids = Array.isArray(bidsResponse.data?.data) ? bidsResponse.data.data : [];
      const verifierProposals = Array.isArray(verifierProposalsResponse.data?.data) ? verifierProposalsResponse.data.data : [];
      setBidReviewCount(contractorBids.length + verifierProposals.length);
    } catch (error) {
      console.error('Failed to fetch bid review count:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      if (response.data.success) {
        setUnreadCount(response.data.data.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    handleProfileMenuClose();
  };

  // Dynamic menu items based on user role (only for authenticated users)
  const getMenuItems = () => {
    if (!user || !isAuthenticated) {
      return [];
    }

    switch (user.role?.toLowerCase()) {
      case 'contractor':
        return [
          { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
          { text: 'Find Work', icon: <Search />, path: '/contractor/marketplace' },
          { text: 'Quotes', icon: <Description />, path: '/bids' },
          { text: 'Active Projects', icon: <Build />, path: '/projects' },
          { text: 'Portfolio', icon: <PhotoLibrary />, path: '/contractor/portfolio' },
          { text: 'History', icon: <Assessment />, path: '/contractor/history' },
          { text: 'Messages', icon: <Message />, path: '/messages', badge: unreadMessages },
          { text: 'Invoices', icon: <ReceiptLong />, path: '/invoices' },
          { text: 'Earnings', icon: <AccountBalance />, path: '/contractor/finances' }
        ];

      case 'verifier':
      case 'professional_verifier':
        return [
          { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
          { text: 'Find Work', icon: <Search />, path: '/verifier/available-verifications' },
          { text: 'My Work', icon: <Assignment />, path: '/verifier/my-work' },
          { text: 'Quote Requests', icon: <RequestQuote />, path: '/received-quotes' },
          { text: 'Portfolio', icon: <PhotoLibrary />, path: '/verifier/portfolio' },
          { text: 'Messages', icon: <Message />, path: '/messages', badge: unreadMessages },
          { text: 'Invoices', icon: <ReceiptLong />, path: '/invoices' },
          { text: 'Earnings', icon: <AttachMoney />, path: '/verifier/earnings' },
          ...(agreementApplicable ? [{ text: 'My Agreement', icon: <Gavel />, path: '/verifier/agreement' }] : [])
        ];

      case 'quality_inspector':
        return [
          { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
          { text: 'Inspections', icon: <Assignment />, path: '/inspector/inspections' },
          { text: 'Reports', icon: <Assessment />, path: '/inspector/reports' },
          { text: 'Earnings', icon: <AttachMoney />, path: '/inspector/earnings' }
        ];

      // VERSION 2: Project Manager Role
      // case 'project_manager':
      //   return [
      //     { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
      //     { text: 'My Managed Projects', icon: <Build />, path: '/projects' },
      //     { text: 'Find Verifiers', icon: <Security />, path: '/verifiers' },
      //     { text: 'Find Project Managers', icon: <SupervisorAccount />, path: '/project-managers' },
      //     { text: 'Messages', icon: <Message />, path: '/messages' },
      //     { text: 'Contractors', icon: <People />, path: '/contractors' },
      //     { text: 'Payments', icon: <AccountBalance />, path: '/payments' }
      //   ];

      case 'merchant':
        return [
          { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
          { text: 'Quote Requests', icon: <Description />, path: '/merchants/quote-requests' },
          { text: 'Orders', icon: <ShoppingCart />, path: '/merchants/orders' },
          { text: 'Products', icon: <Inventory2 />, path: '/merchants/products' },
          { text: 'Messages', icon: <Message />, path: '/messages', badge: unreadMessages },
          { text: 'Earnings', icon: <AccountBalance />, path: '/merchants/payments' },
          { text: 'Documents', icon: <Assignment />, path: '/merchants/documents' },
          { text: 'Reviews', icon: <Star />, path: '/merchants/reviews' },
          { text: 'Profile', icon: <Storefront />, path: '/merchants/profile' }
        ];

      case 'realtor':
        // Without the listings vertical there is nothing for an estate agent to
        // do yet — one honest page instead of a menu of dead ends. Until the
        // flags land, treat it as off so the full menu never flashes by.
        if (!(features.loaded && features.listings)) {
          return [
            { text: 'Estate agent tools', icon: <HomeWork />, path: '/realtor' },
          ];
        }
        return [
          { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
          { text: 'Listings', icon: <HomeWork />, path: '/realtor/listings' },
          { text: 'Messages', icon: <Message />, path: '/messages', badge: unreadMessages },
          { text: 'Documents', icon: <Assignment />, path: '/realtor/documents' },
          { text: 'Profile', icon: <HomeWork />, path: '/realtor/profile' }
        ];

      case 'conveyancer':
        if (!(features.loaded && features.listings)) {
          return [
            { text: 'Conveyancer tools', icon: <Assignment />, path: '/conveyancer' },
          ];
        }
        return [
          { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
          { text: 'Messages', icon: <Message />, path: '/messages', badge: unreadMessages },
          { text: 'Documents', icon: <Assignment />, path: '/conveyancer/documents' },
          { text: 'Profile', icon: <Assignment />, path: '/conveyancer/profile' }
        ];

      case 'admin':
        // The new admin surface carries its own short menu; items get added
        // as they're specified. The legacy console at /admin/dashboard keeps
        // the full menu below.
        if (location.pathname.startsWith('/admin/home')) {
          return [
            {
              // The landing dashboard. `exact` matters: every other item lives
              // under /admin/home/*, so a prefix match would light this up on
              // all of them.
              text: 'Dashboard',
              icon: <Dashboard />,
              path: '/admin/home',
              exact: true,
            },
            {
              // Clickable parent: opens the all-users list, and expands to the
              // per-type lists. Approval state is a filter on those pages, not
              // a nav item.
              text: 'Users',
              icon: <People />,
              path: '/admin/home/users',
              children: [
                { text: 'Clients', icon: <Person />, path: '/admin/home/clients' },
                { text: 'Contractors', icon: <Engineering />, path: '/admin/home/contractors' },
                { text: 'Verifiers', icon: <VerifiedUser />, path: '/admin/home/verifiers' },
              ],
            },
            {
              // Parent opens the whole queue; children are preset role filters
              // on the same page.
              text: 'Applications',
              icon: <Assignment />,
              path: '/admin/home/applications',
              badge: pendingApplications?.total || 0,
              badgeTone: status.neutral,
              children: [
                {
                  text: 'Contractors',
                  icon: <Engineering />,
                  path: '/admin/home/applications?role=contractor',
                  badge: pendingApplications?.contractor || 0,
                  badgeTone: status.neutral,
                },
                {
                  text: 'Verifiers',
                  icon: <VerifiedUser />,
                  path: '/admin/home/applications?role=verifier',
                  badge: pendingApplications?.verifier || 0,
                  badgeTone: status.neutral,
                },
              ],
            },
            { text: 'Client Projects', icon: <Build />, path: '/admin/home/projects' },
            {
              // Parent lands on Work (the default kind); children are the two
              // verification flows.
              text: 'Verifications',
              icon: <VerifiedUser />,
              path: '/admin/home/verifications',
              children: [
                { text: 'Work', icon: <Assignment />, path: '/admin/home/verifications?kind=work' },
                { text: 'Contractor', icon: <Engineering />, path: '/admin/home/verifications?kind=contractor' },
              ],
            },
            ...(features.listings ? [
              { text: 'Listings', icon: <HomeWork />, path: '/admin/home/listings' },
            ] : []),
            {
              // The new surface only covers users, applications, projects and
              // verifications. Everything else still lives in the legacy
              // console, and without this group it is unreachable except by
              // typing a URL — the queue tiles that used to link there are
              // conditional on a non-zero count.
              text: 'Admin console',
              icon: <AdminPanelSettings />,
              children: [
                { text: 'Legacy overview', icon: <Dashboard />, path: '/admin/dashboard' },
                { text: 'Engagement', icon: <TrendingUp />, path: '/admin/engagement' },
                { text: 'Revenue', icon: <AttachMoney />, path: '/admin/revenue' },
                { text: 'Analytics', icon: <Analytics />, path: '/admin/analytics' },
                { text: 'Verifier payouts', icon: <Payment />, path: '/admin/verifier-payouts' },
                { text: 'Verifier pay settings', icon: <AttachMoney />, path: '/admin/verifier-settings' },
                { text: 'Sign-up controls', icon: <ToggleOn />, path: '/admin/signup-settings' },
                { text: 'Merchant price lists', icon: <Storefront />, path: '/admin/merchant-catalogs' },
                { text: 'WhatsApp briefs', icon: <WhatsApp />, path: '/admin/whatsapp-briefs' },
                { text: 'Property check packages', icon: <HomeWork />, path: '/admin/property-check-packages' },
                { text: 'Verification management', icon: <VerifiedUser />, path: '/admin/verification' },
                { text: 'Appeals', icon: <Gavel />, path: '/admin/appeals' },
                { text: 'Reviews', icon: <Star />, path: '/admin/reviews' },
                { text: 'Conversations', icon: <Message />, path: '/admin/conversations' },
                { text: 'Content', icon: <Assessment />, path: '/admin/content' },
                { text: 'Security & audit', icon: <Security />, path: '/admin/security' },
                { text: 'System settings', icon: <Settings />, path: '/admin/settings' },
              ],
            },
          ];
        }
        return [
          // Without this, an admin who opens a legacy page gets the legacy menu
          // and has no route back to the new surface.
          { text: 'New dashboard', icon: <Dashboard />, path: '/admin/home' },
          { text: 'Admin Dashboard', icon: <AdminPanelSettings />, path: '/admin/dashboard' },
          { text: 'User Management', icon: <People />, path: '/admin/users' },
          { text: 'Applications', icon: <Assignment />, path: '/admin/applications' },
          { text: 'Engagement', icon: <TrendingUp />, path: '/admin/engagement' },
          { text: 'Revenue', icon: <AttachMoney />, path: '/admin/revenue' },
          { text: 'Contractor Verifications', icon: <VerifiedUser />, path: '/admin/contractor-verifications' },
          { text: 'Sign-up Controls', icon: <ToggleOn />, path: '/admin/signup-settings' },
          { text: 'Merchant Price Lists', icon: <Storefront />, path: '/admin/merchant-catalogs' },
          { text: 'Verifier Pay Settings', icon: <AttachMoney />, path: '/admin/verifier-settings' },
          { text: 'Verifier Payouts', icon: <Payment />, path: '/admin/verifier-payouts' },
          { text: 'Property Check Packages', icon: <HomeWork />, path: '/admin/property-check-packages' },
          { text: 'System Settings', icon: <Settings />, path: '/admin/settings' },
          { text: 'Security & Audit', icon: <Security />, path: '/admin/security' },
          { text: 'Analytics', icon: <Analytics />, path: '/admin/analytics' },
          { text: 'Content Management', icon: <Assessment />, path: '/admin/content' }
        ];

      case 'client':
      default:
        return [
          { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
          { text: 'My Projects', icon: <Build />, path: '/projects' },
          { text: 'Quotes', icon: <Description />, path: '/offers', badge: bidReviewCount },
          { text: 'Verifications', icon: <VerifiedUser />, path: '/verification-requests' },
          { text: 'Find Contractors', icon: <People />, path: '/contractors' },
          ...(features.listings ? [
            { text: 'Properties', icon: <HomeWork />, path: '/properties' },
            { text: 'How Buying Works', icon: <VerifiedUser />, path: '/buying-property' },
            { text: 'Find a Realtor', icon: <People />, path: '/realtors' },
            { text: 'Find a Conveyancer', icon: <Description />, path: '/conveyancers' },
          ] : []),
          // VERSION 2: { text: 'Find Suppliers', icon: <Store />, path: '/suppliers' },
          { text: 'Find Verifiers', icon: <Security />, path: '/verifiers' },
          // VERSION 2: { text: 'Find Project Managers', icon: <SupervisorAccount />, path: '/project-managers' },
          { text: 'Messages', icon: <Message />, path: '/messages', badge: unreadMessages },
          // VERSION 2: { text: 'My Quote Requests', icon: <CompareArrows />, path: '/quotes' },
          { text: 'Invoices', icon: <ReceiptLong />, path: '/invoices' },
          { text: 'Payments', icon: <AccountBalance />, path: '/payments' }
        ];
    }
  };

  // A nav path may carry a query (e.g. a preset filter). When it does, the
  // pathname must match exactly AND every named param must agree — otherwise
  // ?role=contractor and ?role=verifier would both light up.
  const pathIsActive = (path, exact = false) => {
    const [bare, query] = String(path).split('?');
    if (query) {
      if (location.pathname !== bare) return false;
      const want = new URLSearchParams(query);
      const have = new URLSearchParams(location.search);
      return [...want.keys()].every((key) => have.get(key) === want.get(key));
    }
    return (
      location.pathname === bare ||
      (!exact && bare !== '/dashboard' && bare !== '/messages'
        && location.pathname.startsWith(bare))
    );
  };

  const menuItems = getMenuItems();

  // The group that owns the current route. Until the user expands something
  // themselves, that group is the one shown open.
  const routeGroup = menuItems.find(
    (m) => m.children?.length
      && (m.children.some((c) => pathIsActive(c.path, c.exact)) || (m.path && pathIsActive(m.path, m.exact)))
  )?.text || null;

  const resolvedOpenGroup = openGroup === undefined ? routeGroup : openGroup;
  // Accordion: expanding one group collapses whichever was open.
  const toggleGroup = (key) => setOpenGroup(resolvedOpenGroup === key ? null : key);
  const squareHeaderSurface = {
    borderRadius: 0,
    '&.MuiPaper-root': {
      borderRadius: 0
    }
  };

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, background: brand.gradient, color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
        <Box
          component="img"
          src="/images/logo-eqaya-mark-light.png"
          alt="Eqaya Inc."
          sx={{ height: 34, width: 'auto', display: 'block' }}
        />
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          Construction Marketplace
        </Typography>
      </Box>
      {/* User Role Indicator */}
      <Box sx={{ 
        px: 2, 
        py: 1.5, 
        borderBottom: `1px solid ${border.default}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: bg.subtle
      }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: ['verifier', 'professional_verifier'].includes(user?.role) ? status.success : brand.primary,
            fontSize: '15px',
            fontWeight: 600
          }}
        >
          {headerName?.[0]?.toUpperCase() || 'U'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{
            fontWeight: 600,
            color: '#333',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {headerName}
          </Typography>
          <Box sx={{
            display: 'inline-block',
            px: 1,
            py: 0.25,
            bgcolor: ['verifier', 'professional_verifier'].includes(user?.role) ? `${status.success}15` : `${brand.primary}15`,
            borderRadius: 1,
            mt: 0.5
          }}>
            <Typography variant="caption" sx={{
              color: ['verifier', 'professional_verifier'].includes(user?.role) ? status.success : brand.primary,
              fontWeight: 600,
              textTransform: 'capitalize',
              fontSize: '11px'
            }}>
              {headerSubtitle}
            </Typography>
          </Box>
        </Box>
      </Box>
      <List>
        {menuItems.map((item) => {
          const activeColor = ['verifier', 'professional_verifier'].includes(user?.role) ? status.success : brand.primary;

          // Tone is per-item so a count that means "waiting for you" can read
          // calmly in grey, while genuinely urgent counts keep the red default.
          const badge = (value, tone) => value > 0 && (
            <Box
              sx={{
                minWidth: 22,
                height: 22,
                borderRadius: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 600,
                bgcolor: tone || '#ef4444',
                color: 'white',
                px: 0.75
              }}
            >
              {value}
            </Box>
          );

          // A group: renders as an expandable parent with its own sub-items.
          // Groups have no path of their own — clicking only toggles.
          if (item.children?.length) {
            const childActive = item.children.some((child) => pathIsActive(child.path));
            const selfActive = !!item.path && pathIsActive(item.path, item.exact);
            const isOpen = resolvedOpenGroup === item.text;
            return (
              <React.Fragment key={item.text}>
                <ListItemButton
                  onClick={() => {
                    if (item.path) {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                      // Opening the parent's own page reveals its children and
                      // collapses any other group.
                      setOpenGroup(item.text);
                    } else {
                      toggleGroup(item.text);
                    }
                  }}
                  sx={{
                    backgroundColor:
                      (selfActive && !childActive) || (childActive && !isOpen)
                        ? `${activeColor}15`
                        : 'transparent',
                    borderRight:
                      (selfActive && !childActive) || (childActive && !isOpen)
                        ? `3px solid ${activeColor}`
                        : 'none'
                  }}
                >
                  <ListItemIcon sx={{ color: selfActive || childActive ? activeColor : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{ color: selfActive || childActive ? activeColor : 'inherit' }}
                  />
                  {badge(item.badge, item.badgeTone)}
                  {/* Separate hit area so a clickable parent can still be
                      collapsed without navigating. */}
                  <Box
                    component="span"
                    role="button"
                    aria-label={isOpen ? `Collapse ${item.text}` : `Expand ${item.text}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(item.text);
                    }}
                    sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5, color: text.tertiary }}
                  >
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                </ListItemButton>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => {
                      const isActive = pathIsActive(child.path, child.exact);
                      return (
                        <ListItemButton
                          key={child.text}
                          onClick={() => {
                            navigate(child.path);
                            if (isMobile) setMobileOpen(false);
                            setOpenGroup(item.text);
                          }}
                          sx={{
                            pl: 6,
                            py: 0.75,
                            backgroundColor: isActive ? `${activeColor}15` : 'transparent',
                            borderRight: isActive ? `3px solid ${activeColor}` : 'none'
                          }}
                        >
                          {child.icon && (
                            <ListItemIcon sx={{ minWidth: 32, color: isActive ? activeColor : 'inherit' }}>
                              {child.icon}
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={child.text}
                            primaryTypographyProps={{ fontSize: 14 }}
                            sx={{ color: isActive ? activeColor : text.secondary }}
                          />
                          {badge(child.badge, child.badgeTone)}
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          const isActive = pathIsActive(item.path, item.exact);
          return (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              navigate(item.path);
              if (isMobile) setMobileOpen(false);
              // A plain item belongs to no group, so collapse them all.
              setOpenGroup(null);
            }}
            sx={{
              backgroundColor: isActive ? `${activeColor}15` : 'transparent',
              borderRight: isActive ? `3px solid ${activeColor}` : 'none'
            }}
          >
            <ListItemIcon sx={{ color: isActive ? activeColor : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{ color: isActive ? activeColor : 'inherit' }}
            />
            {badge(item.badge, item.badgeTone)}
          </ListItem>
        );
        })}
      </List>
    </Box>
  );

  // Applicant base path per role: contractors and verifier-type roles each have
  // their own apply/status screens. A not-yet-approved, non-test applicant is
  // locked to those screens — no dashboard, sidebar, or any other route. This is
  // the single chokepoint every route passes through.
  const applicantBase = applicantBaseForRole(user?.role);
  const APPLICATION_PATHS = [
    '/contractor/apply', '/contractor/application-status',
    '/verifier/apply', '/verifier/application-status',
  ];
  const onApplicationPath = APPLICATION_PATHS.includes(location.pathname);
  // Gate ONLY when we definitively know the applicant is unapproved (session
  // validated server-side + explicit pending/rejected). Never on a stale/optimistic
  // cache, or an approved user gets flashed into the wizard until /auth/me resolves.
  const isPendingApplicant = shouldGateApplicant({
    isAuthenticated,
    sessionValidated,
    role: user?.role,
    approvalStatus: user?.approvalStatus || user?.approval_status,
    isTest: user?.isTest === true || user?.is_test === true,
  });
  if (isPendingApplicant && !onApplicationPath) {
    const submitted = !!(user?.applicationSubmittedAt || user?.application_submitted_at);
    return <Navigate to={submitted ? `${applicantBase}/application-status` : `${applicantBase}/apply`} replace />;
  }

  // Full-bleed pages render their own nav/footer — skip Layout chrome. This
  // covers the landing + marketing pages and the contractor application screens.
  const isLanding = (location.pathname === '/' || location.pathname === '/for-contractors' || location.pathname === '/for-verifiers' || location.pathname === '/trust') && !isPublicShortlink;
  // Verify Work booking funnel — render a distraction-free header (logo only),
  // hiding Services / What is Eqaya? / Login / Get Started like a checkout flow.
  const isBookingFunnel = location.pathname.startsWith('/property-check');
  if (isLanding || onApplicationPath) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>
    );
  }

  // Public layout - no sidebar, full-width for unauthenticated users and public profile previews
  if (!isAuthenticated || isPublicShortlink) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {isPublicShortlink && isAuthenticated ? (
          <PublicHeader
            showLinks={false}
            showAuthButtons={false}
            rightSlot={
              <Button
                variant="contained"
                onClick={() => navigate(user?.role === 'contractor' ? '/contractor/portfolio' : '/dashboard')}
                sx={{ bgcolor: '#185451', color: '#fff', fontWeight: 700, px: 2.5, '&:hover': { bgcolor: '#134e4a' } }}
              >
                {user?.role === 'contractor' ? 'Back to Portfolio' : 'Back to Dashboard'}
              </Button>
            }
          />
        ) : isBookingFunnel ? (
          <PublicHeader showLinks={false} showAuthButtons={false} />
        ) : (
          <PublicHeader />
        )}

        {/* Full-width content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            mt: 0,
            backgroundColor: isAdminSurface ? bg.pageLight : bg.page,
            minHeight: 'calc(100vh - 64px)'
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  // Authenticated layout - with sidebar
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - 250px)` },
          ml: { md: `250px` },
          background: brand.gradient,
          ...squareHeaderSurface
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <Box
              component="img"
              src="/images/logo-eqaya-mark-light.png"
              alt="Eqaya Inc."
              sx={{ height: 30, width: 'auto', display: 'block' }}
            />
          </Box>
          
          <Typography variant="h6" sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }}>
            {(() => {
              const p = location.pathname;
              // Exact matches
              const titles = {
                '/': 'Welcome to Eqaya',
                '/dashboard': 'Dashboard',
                '/messages': 'Messages',
                '/projects': 'Projects',
                '/settings': 'Settings',
                '/profile': 'Profile',
                // Verifier routes
                '/verifier/marketplace': 'Find Work',
                '/verifier/my-work': 'My Work',
                '/verifier/assignments': 'My Work',
                '/verifier/history': 'My Work',
                '/verifier/my-verifications': 'My Verifications',
                '/verifier/available-verifications': 'Available Verifications',
                '/verifier/earnings': 'Earnings',
                '/verifier/financial-hub': 'Earnings',
                '/verifier/profile': 'Profile',
                '/verifier/settings': 'Settings',
                '/verifier/help': 'Help',
                '/verifier/agreement': 'Field Verifier Agreement',
                // Admin routes
                '/admin/home': 'Dashboard',
                '/admin/home/users': 'Users',
                '/admin/home/clients': 'Clients',
                '/admin/home/contractors': 'Contractors',
                '/admin/home/verifiers': 'Verifiers',
                '/admin/home/applications': 'Applications',
                '/admin/home/projects': 'Client Projects',
                '/admin/home/verifications': 'Verifications',
                '/admin/dashboard': 'Admin Dashboard',
                '/admin/users': 'User Management',
                '/admin/appeals': 'Appeals',
                '/admin/reviews': 'Reviews',
                '/admin/verification': 'Verification Management',
                // Client routes
                '/client/dashboard': 'Dashboard',
                '/client/projects': 'My Projects',
                // Contractor routes
                '/contractor/dashboard': 'Dashboard',
                '/contractor/marketplace': 'Find Work',
                '/contractor/portfolio': 'Portfolio',
                '/contractor/documents': 'Documents',
                '/contractor/history': 'Work History',
                '/contractor/finances': 'Earnings',
              };
              if (titles[p]) return titles[p];
              // Pattern matches for dynamic routes
              if (p.startsWith('/verifier/verify/')) return 'Verification Workflow';
              if (p.startsWith('/verifier/my-work')) return 'My Work';
              if (p.startsWith('/verifier/simple-verifications/')) return 'Verification Report';
              if (/^\/admin\/home\/users\/[^/]+\/verifications$/.test(p)) return 'Assigned verifications';
              if (p.startsWith('/admin/home/users/')) return 'User';
              if (p.startsWith('/admin/home/applications/')) return 'Application';
              if (p.startsWith('/admin/home/projects/')) return 'Project';
              if (p.startsWith('/admin/')) return 'Admin';
              if (p.startsWith('/project/')) return 'Project Details';
              if (p.startsWith('/messages')) return 'Messages';
              if (p === '/offers') return 'Quotes';
              if (p === '/bids') return 'Quotes';
              if (p.startsWith('/bids/review')) return 'Quotes Received';
              if (p.startsWith('/bids/')) return 'Quote';
              // Fallback: use last meaningful segment
              const segments = p.split('/').filter(Boolean);
              const last = segments[segments.length - 1] || '';
              // Skip numeric IDs — use the segment before
              const label = /^\d+$/.test(last) && segments.length > 1 ? segments[segments.length - 2] : last;
              return label.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Eqaya';
            })()}
          </Typography>

          {/* Notification Bell */}
          <IconButton 
            color="inherit" 
            onClick={() => setNotificationsOpen(true)}
            sx={{ mr: 1 }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          {/* Profile Menu */}
          <IconButton color="inherit" onClick={handleProfileMenuOpen}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
              {headerName?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: { minWidth: 200, mt: 1 }
            }}
          >
            {/* User info header */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {headerName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <MenuItem onClick={() => { navigate('/account-settings'); handleProfileMenuClose(); }}>
              <Settings sx={{ mr: 1.5, fontSize: 20 }} /> Account Settings
            </MenuItem>
            {user?.role === 'contractor' && (
              <MenuItem onClick={() => { navigate('/contractor/documents'); handleProfileMenuClose(); }}>
                <Folder sx={{ mr: 1.5, fontSize: 20 }} /> My Documents
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ExitToApp sx={{ mr: 1.5, fontSize: 20 }} /> Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: 250 }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250, borderRadius: 0 }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250, borderRadius: 0 }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Mobile Bottom Navigation for Project Owners (Clients) */}
      {user?.role === 'client' && (
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTop: `1px solid ${border.light}`,
          bgcolor: 'white'
        }}>
          <BottomNavigation
            value={
              location.pathname === '/dashboard' ? '/dashboard' :
              location.pathname.startsWith('/projects') ? '/projects' :
              location.pathname.startsWith('/bids') ? '/bids/review' :
              location.pathname.startsWith('/messages') ? '/messages' :
              location.pathname.startsWith('/payments') ? '/payments' :
              false
            }
            onChange={(e, newValue) => navigate(newValue)}
            showLabels
            sx={{
              height: 64,
              '& .Mui-selected': { color: brand.primary },
              '& .MuiBottomNavigationAction-root': { minWidth: 'auto', py: 1 }
            }}
          >
            <BottomNavigationAction label="Home" value="/dashboard" icon={<Dashboard />} />
            <BottomNavigationAction label="Projects" value="/projects" icon={<Build />} />
            <BottomNavigationAction
              label="Quotes"
              value="/offers"
              icon={
                <Badge badgeContent={bidReviewCount > 0 ? bidReviewCount : null} color="error">
                  <Description />
                </Badge>
              }
            />
            <BottomNavigationAction
              label="Messages"
              value="/messages"
              icon={
                <Badge badgeContent={unreadMessages > 0 ? unreadMessages : null} color="error">
                  <Message />
                </Badge>
              }
            />
            <BottomNavigationAction label="Payments" value="/payments" icon={<AccountBalance />} />
          </BottomNavigation>
        </Box>
      )}

      {/* Mobile Bottom Navigation for Contractors */}
      {user?.role === 'contractor' && (
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTop: `1px solid ${border.light}`,
          bgcolor: 'white'
        }}>
          <BottomNavigation
            value={
              location.pathname === '/dashboard' ? '/dashboard' :
              location.pathname.startsWith('/contractor/marketplace') ? '/contractor/marketplace' :
              location.pathname.startsWith('/projects') ? '/projects' :
              location.pathname.startsWith('/messages') ? '/messages' :
              location.pathname.startsWith('/contractor/finances') || location.pathname.startsWith('/payments') ? '/contractor/finances' :
              false
            }
            onChange={(e, newValue) => navigate(newValue)}
            showLabels
            sx={{
              height: 64,
              '& .Mui-selected': { color: brand.primary },
              '& .MuiBottomNavigationAction-root': { minWidth: 'auto', py: 1 }
            }}
          >
            <BottomNavigationAction label="Home" value="/dashboard" icon={<Dashboard />} />
            <BottomNavigationAction label="Find Work" value="/contractor/marketplace" icon={<Search />} />
            <BottomNavigationAction label="Projects" value="/projects" icon={<Build />} />
            <BottomNavigationAction label="Messages" value="/messages" icon={<Message />} />
            <BottomNavigationAction label="Earnings" value="/contractor/finances" icon={<AccountBalance />} />
          </BottomNavigation>
        </Box>
      )}

      {/* Mobile Bottom Navigation for Verifiers */}
      {['verifier', 'professional_verifier'].includes(user?.role) && (
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTop: `1px solid ${border.light}`,
          bgcolor: 'white'
        }}>
          <BottomNavigation
            value={
              location.pathname === '/dashboard' ? '/dashboard' :
              location.pathname.startsWith('/verifier/available-verifications') ? '/verifier/available-verifications' :
              location.pathname.startsWith('/verifier/marketplace') ? '/verifier/marketplace' :
              location.pathname.startsWith('/verifier/my-work') ? '/verifier/my-work' :
              location.pathname.startsWith('/verifier/portfolio') ? '/verifier/portfolio' :
              location.pathname.startsWith('/messages') ? '/messages' :
              location.pathname.startsWith('/verifier/profile') ? '/verifier/profile' :
              location.pathname.startsWith('/verifier/earnings') ? '/verifier/earnings' :
              false
            }
            onChange={(e, newValue) => navigate(newValue)}
            showLabels
            sx={{
              height: 64,
              '& .Mui-selected': { color: status.success },
              '& .MuiBottomNavigationAction-root': { minWidth: 'auto', py: 1 }
            }}
          >
            <BottomNavigationAction label="Home" value="/dashboard" icon={<Dashboard />} />
            <BottomNavigationAction label="Find Work" value="/verifier/available-verifications" icon={<Search />} />
            <BottomNavigationAction label="My Work" value="/verifier/my-work" icon={<Assignment />} />
            <BottomNavigationAction label="Portfolio" value="/verifier/portfolio" icon={<PhotoLibrary />} />
            <BottomNavigationAction label="Messages" value="/messages" icon={<Message />} />
            <BottomNavigationAction label="Profile" value="/verifier/profile" icon={<Person />} />
          </BottomNavigation>
        </Box>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - 250px)` },
          mt: 8,
          p: 3,
          backgroundColor: isAdminSurface ? bg.pageLight : bg.page,
          minHeight: 'calc(100vh - 64px)',
          pb: { xs: (['verifier', 'professional_verifier'].includes(user?.role) || user?.role === 'contractor' || user?.role === 'client') ? '80px' : 0, md: 0 }
        }}
      >
        {['verifier', 'professional_verifier'].includes(user?.role) && <OfflineStatusBar />}
        {children}
      </Box>

      {/* Enhanced Notification Center */}
      <EnhancedNotificationCenter 
        open={notificationsOpen} 
        onClose={() => {
          setNotificationsOpen(false);
          fetchUnreadCount(); // Refresh count when closing
        }}
      />
    </Box>
  );
};

export default Layout;
